import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    useMediaQuery, useTheme, TextField, InputAdornment, Chip, Button,
    FormControl, InputLabel, Select, MenuItem,
    Collapse, Tooltip, CircularProgress, TablePagination,
    Menu, ListItemIcon, ListItemText, OutlinedInput, Checkbox
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import PaymentIcon from '@mui/icons-material/Payment';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';

import api from '../services/api';
import { toastSuccess, confirmAcao } from '../services/alerts';

const FORMA_LABEL = {
    PIX: 'PIX', DINHEIRO: 'Dinheiro',
    CARTAO_CREDITO: 'Cartão de Crédito', CARTAO_DEBITO: 'Cartão de Débito',
    BOLETO: 'Boleto', TRANSFERENCIA: 'Transferência',
};

export default function PlanoPagamento() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const [planos, setPlanos] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const [busca, setBusca] = useState('');
    const [debouncedBusca, setDebouncedBusca] = useState('');
    const [formasFilter, setFormasFilter] = useState([]);
    const [ativoFilter, setAtivoFilter] = useState('');
    const [contaBancariaId, setContaBancariaId] = useState('');
    const [banco, setBanco] = useState('');
    const [debouncedBanco, setDebouncedBanco] = useState('');
    const [psicologoId, setPsicologoId] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const [contas, setContas] = useState([]);
    const [psicologos, setPsicologos] = useState([]);
    const [menuAnchor, setMenuAnchor] = useState({ el: null, plano: null });
    const abrirMenu = (e, plano) => setMenuAnchor({ el: e.currentTarget, plano });
    const fecharMenu = () => setMenuAnchor(prev => ({ ...prev, el: null }));

    useEffect(() => {
        const t = setTimeout(() => setDebouncedBusca(busca), 300);
        return () => clearTimeout(t);
    }, [busca]);

    useEffect(() => {
        api.get('/contas-bancarias')
            .then(r => setContas(Array.isArray(r.data) ? r.data : (r.data?.content ?? [])))
            .catch(() => {});
        api.get('/psicologos')
            .then(r => setPsicologos(Array.isArray(r.data) ? r.data : (r.data?.content ?? [])))
            .catch(() => {});
    }, []);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedBanco(banco), 300);
        return () => clearTimeout(t);
    }, [banco]);

    const hasActiveFilters = !!(formasFilter.length || ativoFilter !== '' || contaBancariaId || debouncedBanco || psicologoId);

    const buildParams = useCallback(() => {
        const params = { page, size: rowsPerPage };
        if (debouncedBusca) params.busca = debouncedBusca;
        if (formasFilter.length) params.formasPagamento = formasFilter.join(',');
        if (ativoFilter !== '') params.ativo = ativoFilter;
        if (contaBancariaId) params.contaBancariaId = contaBancariaId;
        if (debouncedBanco) params.banco = debouncedBanco;
        if (psicologoId) params.psicologoId = psicologoId;
        return params;
    }, [page, rowsPerPage, debouncedBusca, formasFilter, ativoFilter, contaBancariaId, debouncedBanco, psicologoId]);

    const carregarDados = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/planos-pagamento/paginado', { params: buildParams() });
            setPlanos(res.data?.content ?? []);
            setTotalElements(res.data?.totalElements ?? 0);
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setLoading(false);
        }
    }, [buildParams]);

    useEffect(() => { carregarDados(); }, [carregarDados]);

    useEffect(() => { setPage(0); }, [debouncedBusca, formasFilter, ativoFilter, contaBancariaId, debouncedBanco, psicologoId]);

    const handleAlterarStatus = async (item) => {
        fecharMenu();
        const inativando = item.ativo;
        const ok = await confirmAcao({
            title: inativando ? 'Inativar plano' : 'Reativar plano',
            text: inativando
                ? 'O plano será inativado e ocultado de seleções padrão, mas o histórico será mantido. Continuar?'
                : 'Reativar este plano de pagamento?',
            confirmButtonText: inativando ? 'Inativar' : 'Reativar',
            icon: inativando ? 'warning' : 'question',
            danger: inativando,
        });
        if (!ok) return;
        try {
            await api.patch(`/planos-pagamento/${item.id_p_pagamento}/${inativando ? 'inativar' : 'reativar'}`);
            toastSuccess(inativando ? 'Plano inativado' : 'Plano reativado');
            carregarDados();
        } catch {
            /* erro exibido pelo handler global */
        }
    };

    const handleExcluir = async (item) => {
        fecharMenu();
        const ok = await confirmAcao({
            title: 'Excluir plano de pagamento',
            text: `Confirma exclusão do plano "${item.nome}"? Esta ação não pode ser desfeita.`,
            confirmButtonText: 'Excluir',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/planos-pagamento/${item.id_p_pagamento}`);
            toastSuccess('Plano excluído');
            carregarDados();
        } catch (err) {
            console.error(err);
        }
    };

    const handleLimparFiltros = () => {
        setFormasFilter([]);
        setAtivoFilter('');
        setContaBancariaId('');
        setBanco('');
        setPsicologoId('');
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} component="div" sx={{ fontWeight: 'bold', color: '#333' }}>
                    Planos de Pagamento
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/plano-pagamento/novo')}>
                    Novo Plano
                </Button>
            </Box>

            <Paper elevation={0} sx={{ p: isMobile ? 1.5 : 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Buscar por nome ou descrição..."
                        variant="outlined"
                        fullWidth
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
                            endAdornment: busca && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setBusca('')} edge="end"><ClearIcon /></IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Tooltip title="Filtros avançados">
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                onClick={() => setShowFilters(!showFilters)}
                                sx={{
                                    border: '1px solid',
                                    borderColor: showFilters || hasActiveFilters ? 'primary.main' : '#e0e0e0',
                                    borderRadius: 2, p: 1.2,
                                    bgcolor: showFilters || hasActiveFilters ? 'rgba(25,118,210,0.08)' : 'transparent',
                                    color: showFilters || hasActiveFilters ? 'primary.main' : 'text.secondary',
                                }}
                            >
                                {showFilters ? <FilterListOffIcon /> : <FilterListIcon />}
                            </IconButton>
                            {hasActiveFilters && !showFilters && (
                                <Box sx={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, bgcolor: 'error.main', borderRadius: '50%' }} />
                            )}
                        </Box>
                    </Tooltip>
                </Box>

                <Collapse in={showFilters}>
                    <Box sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px dashed #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 240 }}>
                            <InputLabel>Formas de Pagamento</InputLabel>
                            <Select
                                multiple
                                value={formasFilter}
                                onChange={(e) => setFormasFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                input={<OutlinedInput label="Formas de Pagamento" />}
                                renderValue={(sel) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {sel.map(v => <Chip key={v} size="small" label={FORMA_LABEL[v] || v} />)}
                                    </Box>
                                )}
                                sx={{ bgcolor: '#fff' }}
                            >
                                {Object.entries(FORMA_LABEL).map(([k, v]) => (
                                    <MenuItem key={k} value={k}>
                                        <Checkbox checked={formasFilter.indexOf(k) > -1} size="small" />
                                        <ListItemText primary={v} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Status</InputLabel>
                            <Select value={ativoFilter} label="Status" onChange={(e) => setAtivoFilter(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value={true}>Ativos</MenuItem>
                                <MenuItem value={false}>Inativos</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 240 }}>
                            <InputLabel>Conta Bancária</InputLabel>
                            <Select value={contaBancariaId} label="Conta Bancária" onChange={(e) => setContaBancariaId(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todas</MenuItem>
                                {contas.map(c => (
                                    <MenuItem key={c.id_c_bancaria} value={c.id_c_bancaria}>
                                        {c.banco} • Ag {c.agencia} • Cc {c.numeroConta}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Banco"
                            size="small"
                            value={banco}
                            onChange={(e) => setBanco(e.target.value)}
                            sx={{ minWidth: 160, bgcolor: '#fff' }}
                        />

                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Psicólogo</InputLabel>
                            <Select value={psicologoId} label="Psicólogo" onChange={(e) => setPsicologoId(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todos</MenuItem>
                                {psicologos.map(p => (
                                    <MenuItem key={p.id_psicologo} value={p.id_psicologo}>{p.nomeCompleto}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {hasActiveFilters && (
                            <Button variant="text" color="error" size="small" onClick={handleLimparFiltros} sx={{ fontWeight: 'bold' }}>
                                Limpar filtros
                            </Button>
                        )}
                    </Box>
                </Collapse>

                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>
                    {totalElements} registro(s){(debouncedBusca || hasActiveFilters) && ' • Filtros aplicados'}
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: '#1ABB9C' }} />
                    </Box>
                ) : (
                <TableContainer>
                    <Table size={isMobile ? 'small' : 'medium'}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell><strong>Nome</strong></TableCell>
                                <TableCell><strong>Forma</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Conta Bancária</strong></TableCell>
                                <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Status</strong></TableCell>
                                <TableCell align="right"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {planos.length > 0 ? planos.map((item) => (
                                <TableRow key={item.id_p_pagamento} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PaymentIcon fontSize="small" color="action" />
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.nome}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {Array.isArray(item.formasPagamento) && item.formasPagamento.length > 0
                                            ? item.formasPagamento.map(f => FORMA_LABEL[f] || f).join(', ')
                                            : '-'}
                                    </TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                        {item.descricaoContaBancaria || '-'}
                                    </TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                        <Chip label={item.ativo ? 'Ativo' : 'Inativo'} color={item.ativo ? 'success' : 'default'} size="small" />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={(e) => abrirMenu(e, item)}>
                                            <MoreVertIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" sx={{ color: '#999' }}>
                                            Nenhum registro encontrado.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                )}

                <Menu anchorEl={menuAnchor.el} open={Boolean(menuAnchor.el)} onClose={fecharMenu}>
                    <MenuItem onClick={() => { fecharMenu(); navigate(`/plano-pagamento/${menuAnchor.plano?.id_p_pagamento}`); }}>
                        <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText>Editar</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleAlterarStatus(menuAnchor.plano)}>
                        <ListItemIcon>
                            {menuAnchor.plano?.ativo
                                ? <BlockIcon fontSize="small" color="warning" />
                                : <CheckCircleIcon fontSize="small" color="success" />}
                        </ListItemIcon>
                        <ListItemText>{menuAnchor.plano?.ativo ? 'Inativar' : 'Reativar'}</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleExcluir(menuAnchor.plano)}>
                        <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText>Excluir</ListItemText>
                    </MenuItem>
                </Menu>

                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    labelRowsPerPage="Linhas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </Paper>
        </Box>
    );
}
